import type { ReactNode } from "react";

/**
 * Renders the small Markdown subset used in intervention texts: `- ` / `* ` /
 * `• ` bullet lines, `**bold**` and `*italic*`. Output is React elements, so
 * text is always escaped.
 */
export function SimpleMarkdown({ text }: { text: string }) {
  const lines = text.replace(/\r\n?/g, "\n").split("\n");
  const blocks: ReactNode[] = [];
  let bullets: ReactNode[] = [];

  const flush = () => {
    if (!bullets.length) return;
    blocks.push(
      <ul key={`list-${blocks.length}`} className="details-md-list">
        {bullets}
      </ul>,
    );
    bullets = [];
  };

  lines.forEach((line, index) => {
    const bullet = line.match(/^\s*[-*•]\s+(.*)$/);
    if (bullet) {
      bullets.push(<li key={index}>{formatInline(bullet[1] ?? "")}</li>);
      return;
    }
    flush();
    blocks.push(<div key={index}>{line.trim() ? formatInline(line) : "\u00A0"}</div>);
  });
  flush();
  return <>{blocks}</>;
}

/** Splits `**bold**` and `*italic*` spans into elements. */
function formatInline(text: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  const pattern = /\*\*(.+?)\*\*|(?<!\*)\*(?!\*)(.+?)\*(?!\*)/g;
  let last = 0;
  for (const match of text.matchAll(pattern)) {
    const start = match.index ?? 0;
    if (start > last) nodes.push(text.slice(last, start));
    nodes.push(
      match[1] !== undefined ? (
        <strong key={start}>{match[1]}</strong>
      ) : (
        <em key={start}>{match[2]}</em>
      ),
    );
    last = start + match[0].length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}
